import { Alert, AutoComplete, Form, Input, Modal, Space, Tabs, Typography, message } from 'antd';
import { useEffect, useMemo, useState } from 'react';
import { api, type SpeakerRow } from '../lib/api';

const { Text } = Typography;

export function AddSpeakerToEventModal({
  open,
  eventId,
  onClose,
  onDone,
}: {
  open: boolean;
  eventId: string;
  onClose: () => void;
  onDone: () => Promise<void> | void;
}) {
  const [tab, setTab] = useState<'existing' | 'new'>('existing');
  const [speakers, setSpeakers] = useState<SpeakerRow[]>([]);
  const [search, setSearch] = useState('');
  const [picked, setPicked] = useState<SpeakerRow | null>(null);
  const [busy, setBusy] = useState(false);
  const [newForm] = Form.useForm<{ name: string; email: string }>();

  useEffect(() => {
    if (!open) return;
    void (async () => {
      try {
        setSpeakers(await api<SpeakerRow[]>('/api/speakers'));
      } catch {
        // ignored — fall back to manual entry
      }
    })();
  }, [open]);

  const options = useMemo(() => {
    const q = search.trim().toLowerCase();
    return speakers
      .filter(
        (s) =>
          !q ||
          s.name.toLowerCase().includes(q) ||
          s.email.toLowerCase().includes(q),
      )
      .map((s) => ({
        value: s.email,
        label: (
          <div>
            <Text strong>{s.name}</Text>{' '}
            <Text type="secondary">{s.email}</Text>
          </div>
        ),
        speaker: s,
      }));
  }, [speakers, search]);

  const handleOk = async () => {
    if (tab === 'existing') {
      if (!picked) {
        message.warning('Pick a speaker first');
        return;
      }
      setBusy(true);
      try {
        await api(`/api/events/${eventId}/speakers`, {
          method: 'POST',
          body: JSON.stringify({ speaker_id: picked.id }),
        });
        await onDone();
        reset();
        onClose();
      } catch (err) {
        message.error((err as { error?: string }).error ?? 'Failed to add');
      } finally {
        setBusy(false);
      }
    } else {
      newForm.submit();
    }
  };

  const reset = () => {
    setPicked(null);
    setSearch('');
    newForm.resetFields();
    setTab('existing');
  };

  const close = () => {
    reset();
    onClose();
  };

  const onCreate = async (values: { name: string; email: string }) => {
    setBusy(true);
    try {
      await api(`/api/events/${eventId}/speakers`, {
        method: 'POST',
        body: JSON.stringify(values),
      });
      await onDone();
      reset();
      onClose();
    } catch (err) {
      message.error((err as { error?: string }).error ?? 'Failed to add');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      title="Add speaker to event"
      open={open}
      onCancel={close}
      onOk={handleOk}
      confirmLoading={busy}
      okText="Add"
    >
      <Tabs
        activeKey={tab}
        onChange={(k) => setTab(k as 'existing' | 'new')}
        items={[
          {
            key: 'existing',
            label: 'From speaker directory',
            children: (
              <Space direction="vertical" size={12} style={{ width: '100%' }}>
                <Text type="secondary">
                  Search the global speaker list. Pick one to attach them to
                  this event.
                </Text>
                <AutoComplete
                  style={{ width: '100%' }}
                  options={options}
                  value={picked ? `${picked.name} <${picked.email}>` : search}
                  onSearch={(v) => {
                    setSearch(v);
                    setPicked(null);
                  }}
                  onSelect={(_v, opt) => {
                    const sp = (opt as unknown as { speaker: SpeakerRow }).speaker;
                    setPicked(sp);
                    setSearch(`${sp.name} <${sp.email}>`);
                  }}
                  placeholder="Search by name or email…"
                  autoFocus
                />
                {picked && (
                  <Alert
                    type="info"
                    message={`Will attach ${picked.name} (${picked.email}) to this event.`}
                  />
                )}
              </Space>
            ),
          },
          {
            key: 'new',
            label: 'New speaker',
            children: (
              <Form form={newForm} layout="vertical" onFinish={onCreate}>
                <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
                  Adds a new speaker to the directory and attaches them to
                  this event.
                </Text>
                <Form.Item
                  name="name"
                  label="Name"
                  rules={[{ required: true, message: 'Please enter a name' }]}
                >
                  <Input autoFocus />
                </Form.Item>
                <Form.Item
                  name="email"
                  label="Email"
                  rules={[
                    { required: true, message: 'Please enter an email' },
                    { type: 'email', message: 'Not a valid email' },
                  ]}
                >
                  <Input />
                </Form.Item>
              </Form>
            ),
          },
        ]}
      />
    </Modal>
  );
}
